import React, { useEffect, useRef } from 'react';

import { useSnackbar } from 'notistack';

import { Alert } from '@mui/material';

import { useGetCMChecklistEvaluation } from '~/hooks/apis/offers/useCMChecklistEvaluation';

import ApiError from '~/types/ApiError';

import LoadedChecklistWorkspace, { type ChecklistContext } from './LoadedChecklistWorkspace';
import { getFailedSourceMessages } from './shared/sourceErrors';

function evaluationErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return 'This offer has not been synced to svc-offer yet.';
  }

  const message = error instanceof ApiError ? error.message : undefined;

  return `Unable to load the CM checklist data${message ? `: ${message}` : ''}`;
}

interface Props extends ChecklistContext {
  isLoadingContext: boolean;
}

export default function CMChecklistWorkspace({ isLoadingContext, ...context }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const surfacedSourceErrorsRef = useRef<Set<string>>(new Set());
  const {
    data: evaluation,
    isLoading: isLoadingEvaluation,
    error: evaluationError,
  } = useGetCMChecklistEvaluation(context.offerId);

  useEffect(() => {
    getFailedSourceMessages(evaluation?.sources ?? []).forEach((message) => {
      if (surfacedSourceErrorsRef.current.has(message)) {
        return;
      }

      surfacedSourceErrorsRef.current.add(message);
      enqueueSnackbar(`${message}. Affected rows require approval.`, { variant: 'error' });
    });
  }, [enqueueSnackbar, evaluation?.sources]);

  if (evaluationError) {
    return <Alert severity="error">{evaluationErrorMessage(evaluationError)}</Alert>;
  }

  if (isLoadingContext || isLoadingEvaluation || !evaluation) {
    return <Alert severity="info">Loading CM checklist data.</Alert>;
  }

  return <LoadedChecklistWorkspace evaluation={evaluation} {...context} />;
}
